import { SessionStore } from '~/stores/session'
import { createFetch, type FetchContext, type FetchResponse } from 'ofetch'

const SAFE_METHODS = ['GET', 'HEAD', 'OPTIONS', 'TRACE']
const ANTIFORGERY_FAILURE = 'AntiforgeryValidationFailed'

export class FetchFactory {
  static use() {
    // const cookieJar = CookieJar.use()
    const session = SessionStore.use()
    const antiforgery = Antiforgery.use()
    const config = useRuntimeConfig()

    // Both gateways issue their own CSRF token, so requests are matched back to the gateway they are
    // going to. PxApi swaps localhost -> 127.0.0.1 for server-side calls, so match either form.
    const gatewayBaseUrls = [config.public.pxApiBaseUrl, config.public.engineApiBaseUrl]
      .filter((base) => !_isNil(base) && base !== '')
      .flatMap((base: string) =>
        base.includes('localhost') ? [base, base.replace('localhost', '127.0.0.1')] : [base]
      )

    const resolveGateway = (request: any) => {
      const url = typeof request === 'string' ? request : request?.url
      if (_isNil(url)) return undefined

      return gatewayBaseUrls.find((base) => String(url).startsWith(base))
    }

    async function setAntiforgeryHeader(context: FetchContext) {
      const method = (context.options.method ?? 'GET').toUpperCase()
      if (SAFE_METHODS.includes(method)) return

      const baseUrl = resolveGateway(context.request)
      if (_isNil(baseUrl)) return

      const token = await antiforgery.ensure(baseUrl)
      if (_isNil(token)) return

      context.options.headers = {
        ...context.options.headers,
        'X-XSRF-TOKEN': token
      }
    }

    /**
     * Retries a request once with a fresh CSRF token when the gateway rejects the one we sent, so a
     * token that outlived its paired cookie costs a silent retry rather than a failed user action.
     */
    const withAntiforgeryRetry = (instance: any) => {
      const wrapped: any = (url: any, options?: any) => instance(url, options)

      wrapped.raw = async (url: any, options?: any) => {
        const response = await instance.raw(url, options)

        if (response?.status !== 400 || response?._data?.type !== ANTIFORGERY_FAILURE) {
          return response
        }

        const baseUrl = resolveGateway(url)
        if (_isNil(baseUrl)) return response

        antiforgery.clear(baseUrl)

        // onRequest runs again and picks up the refreshed token.
        return instance.raw(url, options)
      }

      wrapped.native = instance.native
      wrapped.create = instance.create

      return wrapped
    }

    function setXProfileId(context: FetchContext) {
      if (!_isNil(session.profileId)) {
        context.options.headers = {
          ...context.options.headers,
          'X-Profile-Id': session.profileId
        }
      }
    }

    function setAnalyticsHeaders(context: FetchContext) {
      if (process.client) {
        const { getAnonymousId, getSessionId } = Analytics.use()
        const anonId = getAnonymousId()
        const sessionId = getSessionId()

        let headers = {}

        if (!_isNil(anonId) && !_isNil(sessionId)) {
          headers = {
            'X-Analytics-Id': anonId,
            'X-Analytics-Session': sessionId
          }
        }

        const fbc = useCookie('_fbc').value
        if (!_isNil(fbc)) {
          headers = {
            ...headers,
            'X-Analytics-Fbc': fbc
          }
        }

        const fbp = useCookie('_fbp').value
        if (!_isNil(fbp)) {
          headers = {
            ...headers,
            'X-Analytics-Fbp': fbp
          }
        }

        const campaign = session.params['utm_campaign']
        if (!_isNil(campaign)) {
          headers = {
            ...headers,
            'X-Analytics-Campaign': campaign
          }
        }

        const source = session.params['utm_source']
        if (!_isNil(source)) {
          headers = {
            ...headers,
            'X-Analytics-Source': source
          }
        }

        const medium = session.params['utm_medium']
        if (!_isNil(medium)) {
          headers = {
            ...headers,
            'X-Analytics-Medium': medium
          }
        }

        const term = session.params['utm_term']
        if (!_isNil(term)) {
          headers = {
            ...headers,
            'X-Analytics-Term': term
          }
        }

        const content = session.params['utm_content']
        if (!_isNil(content)) {
          headers = {
            ...headers,
            'X-Analytics-Content': content
          }
        }

        const gclid = session.params['gclid']
        if (!_isNil(gclid)) {
          headers = {
            ...headers,
            'X-Analytics-Gclid': gclid
          }
        }

        const wbraid = session.params['wbraid']
        if (!_isNil(wbraid)) {
          headers = {
            ...headers,
            'X-Analytics-Wbraid': wbraid
          }
        }

        const msclkid = session.params['MSCLKID']
        if (!_isNil(msclkid)) {
          headers = {
            ...headers,
            'X-Analytics-Msclkid': msclkid
          }
        }

        context.options.headers = {
          ...context.options.headers,
          ...headers
        }
      }
    }

    const _fetch = createFetch()

    const make$$fetch = (
      onReq?: (ctx: FetchContext) => void,
      onResp?: (ctx: FetchContext & { response: FetchResponse<ResponseType> }) => void
    ) =>
      _fetch.create({
        credentials: 'include' as RequestCredentials,
        onResponseError: function (context: any & { error: Error }): Promise<void> | void {
          console.log('RESPONSE ERROR', context)
        },
        onRequestError(context: any & { error: Error }): Promise<void> | void {
          console.log('REQUEST ERROR', context)
        },
        async onRequest(context: FetchContext): Promise<void> {
          onReq && onReq(context)
          setXProfileId(context)
          setAnalyticsHeaders(context)
          await setAntiforgeryHeader(context)
        },
        onResponse(
          context: FetchContext & { response: FetchResponse<ResponseType> }
        ): Promise<void> | void {
          context.options.ignoreResponseError = true
          onResp && onResp(context)
          // cookieJar.touch()
        }
      })

    if (process.client) {
      const cookieJar = CookieJar.use()
      // const { getRequestTracker } = SessionReplay.use()

      const $$fetch = () => {
        // const tracker = getRequestTracker()
        return withAntiforgeryRetry(
          make$$fetch(
            (_) => {
              // tracker.start()
            },
            (_) => {
              cookieJar.touch()
              // tracker.end(ctx)
            }
          )
        )
      }

      return {
        $$fetch
      }
    } else {
      const $$fetch = () => {
        return withAntiforgeryRetry(
          make$$fetch((ctx) => {
            ctx.options.headers = useRequestHeaders(['cookie'])
          })
        )
      }

      return {
        $$fetch
      }
    }
  }
}
